import { useMemo } from 'react';

import { tokens } from '@cast/design-system';
import { SeeMoreButton } from '@cast/features-cluster-ui';
import { formatCurrency } from '@cast/utils';
import {
  ActionRequired,
  useNetworkSummaryQuery,
} from '@cast/split-me/features/cluster/cost-report';
import { SegmentedMetricCard } from '../_components/SegmentedMetricCard';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { NETWORK_COST_SORT } from '../_utils/dashboardListNavigation';
import { useNetworkCostAdjustedDateRange } from './_hooks/useNetworkCostAdjustedDateRange';

const formatCost = (value: number | undefined) =>
  value === undefined
    ? '--'
    : formatCurrency(value, { maximumFractionDigits: 2 });

const EGRESS_COLOR = tokens.primitives.colors.blue['600'];
const INGRESS_COLOR = tokens.primitives.colors.blue['200'];

export const NetworkCostCard = () => {
  const { adjustedDateRange } = useNetworkCostAdjustedDateRange();
  const {
    data,
    error,
    refetch,
    isLoading,
    isFetching,
    isCapabilitiesLoading,
    hasNetworkAgent,
  } = useNetworkSummaryQuery({ adjustedDateRange });
  const { navigateToNetworkList } = useInventoryWorkloadsDashboardNavigation();

  const seeMore = (
    <SeeMoreButton onClick={() => navigateToNetworkList(NETWORK_COST_SORT)} />
  );

  const segments = useMemo(
    () => [
      {
        label: 'Egress',
        value: data?.cost.egress ?? 0,
        color: EGRESS_COLOR,
        testId: 'network-cost-egress',
      },
      {
        label: 'Ingress',
        value: data?.cost.ingress ?? 0,
        color: INGRESS_COLOR,
        testId: 'network-cost-ingress',
      },
    ],
    [data]
  );

  if (!isCapabilitiesLoading && !hasNetworkAgent) {
    return (
      <WidgetLayout
        title="Network cost"
        testId="network-cost-card"
        headerDivider={false}
        headerEndContent={seeMore}
        slotProps={{ header: { py: 12 } }}
      >
        <ActionRequired />
      </WidgetLayout>
    );
  }

  return (
    <SegmentedMetricCard
      title="Network cost"
      total={data?.cost.total}
      formatValue={formatCost}
      segments={segments}
      testId="network-cost-card"
      metricTestId="network-cost-total"
      barTestId="network-cost-bar"
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      refresh={refetch}
      headerEndContent={seeMore}
    />
  );
};
